import { EditableItemsAction, EditableItemsActionKeys } from '../actions/editableItems';
import { UiAction, UiActionKeys } from '../actions/ui';
import { IndesignTextFieldsConfig } from '../utils/indesignEditor/editableTextFields/getData';
import { HtmlTextFieldsConfig } from '../utils/htmlEditor/editableTextFields/getData';
import { IndesignMediaElementConfig } from '../utils/indesignEditor/editableMedia/getData';
import { HtmlMediaElementConfig } from '../utils/htmlEditor/editableMedia/getData';
import { LinkConfig } from '../utils/editableLinks/getData';
import { ButtonConfig } from '../utils/indesignEditor/editableButtons/getData';
import { IframeConfig } from '../utils/indesignEditor/editableIframes/getData';
import { EnrichmentConfig } from '../utils/indesignEditor/editableEnrichments/getData';
import { SliderConfig } from '../utils/indesignEditor/editableSliders/getData';
import { ThemedItemConfig } from '../utils/indesignEditor/themedItems/getData';
import { DuplicatedItemInSpreadConfig } from '../utils/indesignEditor/html';
import { INITIAL_PAGE } from './constants';
import Utils from '../../../utils';

export interface EditableTextFieldsState {
	[k: number]: {
		[k: string]: IndesignTextFieldsConfig | HtmlTextFieldsConfig;
	};
}

export interface EditableMediaState {
	[k: number]: {
		[k: string]: IndesignMediaElementConfig | HtmlMediaElementConfig;
	};
}

export interface EditableLinksState {
	[k: number]: {
		[k: string]: LinkConfig;
	};
}

export interface EditableButtonsState {
	[k: number]: {
		[k: string]: ButtonConfig;
	};
}

export interface EditableIframesState {
	[k: number]: {
		[k: string]: IframeConfig;
	};
}

export interface EditableEnrichmentsState {
	[k: number]: {
		[k: string]: EnrichmentConfig;
	};
}

export interface EditableSlidersState {
	[k: number]: {
		[k: string]: SliderConfig;
	};
}

export interface ThemedItemsState {
	[k: number]: {
		[k: string]: ThemedItemConfig;
	};
}

export interface DuplicatedItemsInSpreadState {
	[k: number]: {
		[k: string]: DuplicatedItemInSpreadConfig;
	};
}

type EditableItemsState =
	EditableTextFieldsState |
	EditableMediaState |
	EditableLinksState |
	EditableButtonsState |
	EditableIframesState |
	EditableEnrichmentsState |
	ThemedItemsState |
	DuplicatedItemsInSpreadState;

type UpdateEditableItemActionKeys =
	EditableItemsActionKeys.UPDATE_EDITABLE_TEXT_FIELD |
	EditableItemsActionKeys.UPDATE_EDITABLE_MEDIA |
	EditableItemsActionKeys.UPDATE_EDITABLE_LINK |
	EditableItemsActionKeys.UPDATE_EDITABLE_BUTTON |
	EditableItemsActionKeys.UPDATE_EDITABLE_IFRAME |
	EditableItemsActionKeys.UPDATE_EDITABLE_ENRICHMENT |
	EditableItemsActionKeys.UPDATE_EDITABLE_SLIDER;

enum EditableItemName {
	TEXT_FIELD = 'editableTextFields',
	MEDIA = 'editableMedia',
	LINK = 'editableLinks',
	BUTTON = 'editableButtons',
	IFRAME = 'editableIframes',
	ENRICHMENT = 'editableEnrichments',
	SLIDER = 'editableSliders',
	THEMED_ITEM = 'themedItems',
	DUPLICATED_ITEM_IN_SPREAD = 'duplicatedItemsInSpread',
}

const editableItemActionMap = new Map<EditableItemName, UpdateEditableItemActionKeys | undefined>([
	[
		EditableItemName.TEXT_FIELD,
		EditableItemsActionKeys.UPDATE_EDITABLE_TEXT_FIELD,
	],
	[
		EditableItemName.MEDIA,
		EditableItemsActionKeys.UPDATE_EDITABLE_MEDIA,
	],
	[
		EditableItemName.LINK,
		EditableItemsActionKeys.UPDATE_EDITABLE_LINK,
	],
	[
		EditableItemName.BUTTON,
		EditableItemsActionKeys.UPDATE_EDITABLE_BUTTON,
	],
	[
		EditableItemName.IFRAME,
		EditableItemsActionKeys.UPDATE_EDITABLE_IFRAME,
	],
	[
		EditableItemName.ENRICHMENT,
		EditableItemsActionKeys.UPDATE_EDITABLE_ENRICHMENT,
	],
	[
		EditableItemName.SLIDER,
		EditableItemsActionKeys.UPDATE_EDITABLE_SLIDER,
	],
	[
		EditableItemName.THEMED_ITEM,
		undefined,
	],
	[
		EditableItemName.DUPLICATED_ITEM_IN_SPREAD,
		undefined,
	],
]);

const editableTextFieldsInitialState: EditableTextFieldsState = {
	[INITIAL_PAGE]: {},
};

const editableMediaInitialState: EditableMediaState = {
	[INITIAL_PAGE]: {},
};

const editableLinksInitialState: EditableLinksState = {
	[INITIAL_PAGE]: {},
};

const editableButtonsInitialState: EditableButtonsState = {
	[INITIAL_PAGE]: {},
};

const editableIframesInitialState: EditableIframesState = {
	[INITIAL_PAGE]: {},
};

const editableEnrichmentsInitialState: EditableEnrichmentsState = {
	[INITIAL_PAGE]: {},
};

const editableSlidersInitialState: EditableSlidersState = {
	[INITIAL_PAGE]: {},
};

const themedItemsInitialState: ThemedItemsState = {
	[INITIAL_PAGE]: {},
};

const duplicatedItemsInSpreadInitialState: DuplicatedItemsInSpreadState = {
	[INITIAL_PAGE]: {},
};

export const createEditableItemReducer = <stateType extends EditableItemsState>(initialState: stateType, editableItemName: EditableItemName) => {
	return function editableItem(
		state: stateType = initialState,
		action: EditableItemsAction | UiAction,
	): stateType {
		const updateAction = editableItemActionMap.get(editableItemName);

		switch (action.type) {
			case EditableItemsActionKeys.SET_EDITABLE_ITEMS_DATA:
				if (!action.data[editableItemName]) {
					return state;
				}

				return {
					...(state as EditableItemsState),
					[action.page]: {
						...Utils.itemsToById(action.data[editableItemName]!),
					},
				} as stateType;
			case updateAction:
				return {
					...(state as EditableItemsState),
					[action.page]: {
						...state[action.page],
						[action.id]: {
							...state[action.page][action.id],
							...action.data,
							isEditedFromBackoffice: 'isEditedFromBackoffice' in action.data && action.data.isEditedFromBackoffice !== undefined
								? action.data.isEditedFromBackoffice
								: true,
						},
					},
				} as stateType;
			case UiActionKeys.RESET:
				return initialState;
			default:
				return state;
		}
	};
};

export const editableTextFields = createEditableItemReducer<EditableTextFieldsState>(editableTextFieldsInitialState, EditableItemName.TEXT_FIELD);
export const editableMedia = createEditableItemReducer<EditableMediaState>(editableMediaInitialState, EditableItemName.MEDIA);
export const editableLinks = createEditableItemReducer<EditableLinksState>(editableLinksInitialState, EditableItemName.LINK);
export const editableButtons = createEditableItemReducer<EditableButtonsState>(editableButtonsInitialState, EditableItemName.BUTTON);
export const editableIframes = createEditableItemReducer<EditableIframesState>(editableIframesInitialState, EditableItemName.IFRAME);
export const editableEnrichments = createEditableItemReducer<EditableEnrichmentsState>(editableEnrichmentsInitialState, EditableItemName.ENRICHMENT);
export const editableSliders = createEditableItemReducer<EditableSlidersState>(editableSlidersInitialState, EditableItemName.SLIDER);
export const themedItems = createEditableItemReducer<ThemedItemsState>(themedItemsInitialState, EditableItemName.THEMED_ITEM);
export const duplicatedItemsInSpread = createEditableItemReducer<DuplicatedItemsInSpreadState>(duplicatedItemsInSpreadInitialState, EditableItemName.DUPLICATED_ITEM_IN_SPREAD);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/reducer/editableItems.ts